import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { CheckCircle2, Loader2, Phone, PhoneCall, X } from 'lucide-react';
import { Button } from '../index';
import { hulplijnApi, HULPLIJN_KEY, type HulplijnVerzoek } from '../../services/veiligeHavenApi';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

/**
 * 🚨 Directe Hulplijn (onderdeel 60, Onderdeel 4): asynchrone spoed-terugbelgrendel.
 * De medewerker kiest een discipline en laat een terugbelnummer achter; het
 * verzoek gaat naar álle professionals van die discipline in de bedrijfspool
 * en de eerste die het claimt belt terug. Niet aan de lijn wachten: direct het
 * rust-scherm.
 */
export const DirecteHulplijnKnop: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [discipline, setDiscipline] = useState<string>('');
  const [nummer, setNummer] = useState('');
  const [ontvangen, setOntvangen] = useState(false);

  const status = useQuery({
    queryKey: HULPLIJN_KEY,
    queryFn: hulplijnApi.status,
    refetchInterval: (q) => (q.state.data?.verzoeken.some((v) => v.status === 'OPEN') ? 15_000 : false),
    retry: false,
  });
  const disciplines = status.data?.disciplines ?? [];
  const lopend = (status.data?.verzoeken ?? []).filter((v) => v.status === 'OPEN' || v.status === 'CLAIMED');

  const verzoek = useMutation({
    mutationFn: () => hulplijnApi.verzoek(discipline, nummer.trim()),
    onSuccess: () => {
      setOntvangen(true);
      setNummer('');
      queryClient.invalidateQueries({ queryKey: HULPLIJN_KEY });
    },
  });

  if (status.isLoading || disciplines.length === 0) return null;

  const openSheet = () => {
    setDiscipline(disciplines[0]?.professional_type ?? '');
    setOntvangen(false);
    verzoek.reset();
    setOpen(true);
  };

  return (
    <section aria-label={t('hulplijn.titel', { defaultValue: 'Directe Hulplijn' })} className="space-y-2">
      {lopend.map((v) => (
        <LopendVerzoek key={v.id} verzoek={v} />
      ))}

      <button
        type="button"
        onClick={openSheet}
        className="w-full flex items-center gap-4 rounded-2xl border border-rose-200 bg-rose-50 hover:bg-rose-100 active:bg-rose-200/60 px-5 py-4 text-left shadow-sm transition-colors"
      >
        <span className="w-12 h-12 rounded-full bg-rose-500 text-white flex items-center justify-center shrink-0 shadow-sm">
          <PhoneCall className="w-6 h-6" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-base sm:text-lg font-bold text-rose-900">{t('hulplijn.knop', { defaultValue: 'Directe Hulplijn' })}</span>
          <span className="block text-sm text-rose-800/90">
            {t('hulplijn.knopSub', { defaultValue: 'Word met spoed teruggebeld door een expert uit jouw bedrijfspool. Anoniem, je hoeft niet aan de lijn te wachten.' })}
          </span>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-labelledby="hulplijn-titel">
          <div className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-[#FCFBF8] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#E9E4D8] px-4 py-3">
              <h2 id="hulplijn-titel" className="font-semibold text-gray-900 flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-rose-500" aria-hidden="true" /> {t('hulplijn.titel', { defaultValue: 'Directe Hulplijn' })}
              </h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600" aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })}>
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {ontvangen ? (
              <div className="p-6 text-center space-y-4" role="status">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" aria-hidden="true" />
                <p className="text-base text-gray-900 leading-relaxed">
                  🎉{' '}
                  {t('hulplijn.ontvangen', {
                    defaultValue:
                      'Uw spoed-verzoek is succesvol ontvangen door de professionals uit uw bedrijfspool. Een van de gecertificeerde experts neemt zo spoedig mogelijk telefonisch contact met u op via een afgeschermd, anoniem nummer.',
                  })}
                </p>
                <p className="text-xs text-gray-500">{t('hulplijn.chatOpen', { defaultValue: 'Zodra een expert je verzoek oppakt, kun je ook direct chatten via Mijn Veilige Gesprekken.' })}</p>
                <Button className="w-full" onClick={() => setOpen(false)}>
                  {t('algemeen.sluiten', { defaultValue: 'Sluiten' })}
                </Button>
              </div>
            ) : (
              <form
                className="p-4 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (discipline && nummer.trim()) verzoek.mutate();
                }}
              >
                <fieldset>
                  <legend className="block text-xs font-medium text-gray-700 mb-1.5">{t('hulplijn.discipline', { defaultValue: 'Met wie wil je spreken?' })}</legend>
                  <div className="grid gap-2">
                    {disciplines.map((d) => {
                      const loopt = lopend.some((v) => v.professional_type === d.professional_type && v.status === 'OPEN');
                      return (
                        <label
                          key={d.professional_type}
                          className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm ${
                            loopt ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          } ${discipline === d.professional_type ? 'border-rose-400 bg-white ring-1 ring-rose-200' : 'border-gray-200 bg-white'}`}
                        >
                          <input
                            type="radio"
                            name="hulplijn-discipline"
                            value={d.professional_type}
                            checked={discipline === d.professional_type}
                            disabled={loopt}
                            onChange={() => setDiscipline(d.professional_type)}
                            className="accent-rose-500"
                          />
                          <span className="flex-1 font-medium text-gray-900">{d.label}</span>
                          <span className="text-xs text-gray-500">
                            {loopt ? t('hulplijn.loopt', { defaultValue: 'verzoek loopt' }) : t('hulplijn.experts', { defaultValue: '{{n}} expert(s)', n: d.aantal })}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <label className="block text-sm">
                  <span className="block text-xs font-medium text-gray-700 mb-1">{t('hulplijn.nummer', { defaultValue: 'Op welk nummer mogen we je terugbellen?' })}</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={nummer}
                    onChange={(e) => setNummer(e.target.value)}
                    placeholder="06 12345678"
                    maxLength={25}
                    required
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                  />
                  <span className="block mt-1 text-[11px] text-gray-500">
                    {t('hulplijn.nummerPrivacy', {
                      defaultValue: 'Alleen de expert die je verzoek oppakt ziet dit nummer. Het staat versleuteld opgeslagen en wordt na het gesprek gewist. Je werkgever ziet niets.',
                    })}
                  </span>
                </label>

                <a href="tel:112" className="flex items-center gap-2 rounded-lg border border-rose-200 bg-white p-2.5 text-xs text-rose-900">
                  <Phone className="w-4 h-4 text-rose-500 shrink-0" aria-hidden="true" />
                  {t('hulplijn.112', { defaultValue: 'Direct levensgevaar? Bel altijd eerst 112.' })}
                </a>

                {verzoek.isError && (
                  <p className="text-sm text-red-600" role="alert">
                    {foutMelding(verzoek.error, t('hulplijn.fout', { defaultValue: 'Je verzoek kon niet worden verstuurd. Probeer het opnieuw.' }))}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)}>
                    {t('algemeen.annuleren', { defaultValue: 'Annuleren' })}
                  </Button>
                  <Button type="submit" className="flex-1" disabled={!discipline || !nummer.trim()} loading={verzoek.isPending}>
                    {t('hulplijn.versturen', { defaultValue: 'Bel mij terug' })}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const LopendVerzoek: React.FC<{ verzoek: HulplijnVerzoek }> = ({ verzoek }) => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const annuleer = useMutation({
    mutationFn: () => hulplijnApi.annuleer(verzoek.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: HULPLIJN_KEY }),
  });

  if (verzoek.status === 'CLAIMED') {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" role="status">
        <p className="flex items-center gap-2 font-semibold text-emerald-900">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" aria-hidden="true" />
          {t('hulplijn.opgepakt', { defaultValue: '{{naam}} heeft je verzoek opgepakt', naam: verzoek.professional_naam ?? verzoek.discipline_label })}
        </p>
        <p className="mt-1 text-sm text-emerald-900/80">
          {t('hulplijn.opgepaktSub', { defaultValue: 'Je wordt zo spoedig mogelijk gebeld vanaf een afgeschermd nummer. Je kunt ook al chatten via Mijn Veilige Gesprekken.' })}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-rose-200 bg-white p-4" role="status" aria-live="polite">
      <p className="flex items-center gap-2 font-semibold text-gray-900">
        <Loader2 className="w-5 h-5 text-rose-500 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {t('hulplijn.wacht', { defaultValue: 'Spoed-verzoek ontvangen — {{discipline}}', discipline: verzoek.discipline_label })}
      </p>
      <p className="mt-1 text-sm text-gray-600">
        {t('hulplijn.wachtSub', { defaultValue: 'De experts uit je bedrijfspool zijn gealarmeerd. Een van hen belt je zo spoedig mogelijk terug.' })}
      </p>
      <button
        type="button"
        onClick={() => annuleer.mutate()}
        disabled={annuleer.isPending}
        className="mt-2 text-xs font-medium text-gray-500 underline hover:text-gray-800 disabled:opacity-50"
      >
        {t('hulplijn.annuleer', { defaultValue: 'Verzoek intrekken' })}
      </button>
    </div>
  );
};

export default DirecteHulplijnKnop;
